import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';
import type { PackageOffer, PackageOfferUnit } from './SeekerDetailModal/types';
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import { formatTimeInTimezone } from '../utils/timezoneUtils';

type AvailabilitySlot = {
  time: string;
  available: boolean;
  start_utc?: string;
  end_utc?: string;
  guide_timezone?: string;
};

type AvailabilityDay = {
  date: string;
  dayOfWeek?: string;
  timeSlots: AvailabilitySlot[];
};

interface SessionSelection {
  unit: PackageOfferUnit & { offerName: string };
  date: string;
  time: string;
  startUtc: string;
  durationMinutes: number;
}

interface BatchScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  guideId: string | null;
  seekerId: string | null;
  seekerName?: string | null;
  seekerEmail?: string | null;
  packageOffers: PackageOffer[];
  onScheduled: (sessionIds: string[]) => void;
}

const AVAILABILITY_WINDOW_DAYS = 60;

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (date: string): string => {
  try {
    const dateObj = new Date(`${date}T00:00:00`);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return date;
  }
};

const formatTimeForDisplay = (slot: AvailabilitySlot, guideTimezone: string): string => {
  if (slot.start_utc && slot.guide_timezone) {
    return formatTimeInTimezone(slot.start_utc, guideTimezone);
  }

  const [hours, minutes] = slot.time.split(':').map((value) => Number.parseInt(value, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return slot.time;
  }
  const dateObj = new Date();
  dateObj.setHours(hours, minutes, 0, 0);
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const sortAvailabilityDays = (days: AvailabilityDay[]): AvailabilityDay[] => {
  return [...days].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

const canFitSession = (
  timeSlots: AvailabilitySlot[],
  startTime: string,
  durationMinutes: number
): boolean => {
  const startIndex = timeSlots.findIndex(slot => slot.time === startTime);
  if (startIndex === -1 || !timeSlots[startIndex].available) {
    return false;
  }

  const slotsNeeded = Math.ceil(durationMinutes / 15);

  for (let i = 0; i < slotsNeeded; i++) {
    const slotIndex = startIndex + i;
    if (slotIndex >= timeSlots.length || !timeSlots[slotIndex].available) {
      return false;
    }
  }

  return true;
};

const getUnitDuration = (unit: PackageOfferUnit): number => {
  if (unit.durationMinutes) {
    return unit.durationMinutes;
  }
  const componentType = unit.componentType?.toLowerCase() || '';
  if (componentType === 'ceremony' || componentType === 'journey') {
    return 240;
  }
  return 60;
};

const BatchScheduleModal: React.FC<BatchScheduleModalProps> = ({
  isOpen,
  onClose,
  guideId,
  seekerId,
  seekerName,
  seekerEmail,
  packageOffers,
  onScheduled,
}) => {
  const { timezone: guideTimezone } = useGuideTimezone();
  const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => new Date());

  // Batch scheduling state
  const [selectedSessions, setSelectedSessions] = useState<SessionSelection[]>([]);
  const [currentUnit, setCurrentUnit] = useState<(PackageOfferUnit & { offerName: string; offerId: string }) | null>(null);
  const [currentDate, setCurrentDate] = useState<string>('');
  const [step, setStep] = useState<'select-units' | 'schedule-session'>('select-units');

  const acceptedOffers = useMemo(
    () => packageOffers.filter(offer => offer.status === 'accepted'),
    [packageOffers]
  );

  const availableUnits = useMemo(() => {
    return acceptedOffers.flatMap(offer =>
      offer.units
        .filter(unit => unit.status === 'unallocated')
        .map(unit => ({ ...unit, offerName: offer.name, offerId: offer.id }))
    );
  }, [acceptedOffers]);

  // Units that haven't been scheduled in this batch yet
  const remainingUnits = useMemo(() => {
    const scheduledUnitIds = new Set(selectedSessions.map(s => s.unit.id));
    return availableUnits.filter(unit => !scheduledUnitIds.has(unit.id));
  }, [availableUnits, selectedSessions]);

  const normalizedToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const startOfCalendar = useMemo(() => {
    const firstOfMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - start.getDay());
    return start;
  }, [displayedMonth]);

  const goToPrevMonth = () => {
    setDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const canGoPrevMonth = useMemo(() => {
    const prevMonth = new Date(displayedMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const currentMonth = new Date(normalizedToday.getFullYear(), normalizedToday.getMonth(), 1);
    const prevMonthStart = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    return prevMonthStart >= currentMonth;
  }, [displayedMonth, normalizedToday]);

  const currentDurationMinutes = currentUnit ? getUnitDuration(currentUnit) : 60;

  // Check if a slot conflicts with already-selected sessions
  const isSlotConflicting = (date: string, startUtc: string, durationMinutes: number): boolean => {
    const proposedStart = new Date(startUtc);
    const proposedEnd = new Date(proposedStart.getTime() + durationMinutes * 60 * 1000);

    for (const session of selectedSessions) {
      const sessionStart = new Date(session.startUtc);
      const sessionEnd = new Date(sessionStart.getTime() + session.durationMinutes * 60 * 1000);

      if (proposedStart < sessionEnd && proposedEnd > sessionStart) {
        return true;
      }
    }
    return false;
  };

  const calendarDays = useMemo(() => {
    const days: Array<{
      dateKey: string;
      label: number;
      isPast: boolean;
      hasSlots: boolean;
      isSelected: boolean;
      isCurrentMonth: boolean;
      hasScheduledSession: boolean;
    }> = [];

    const scheduledDates = new Set(selectedSessions.map(s => s.date));

    for (let i = 0; i < 42; i += 1) {
      const currentDate = new Date(startOfCalendar);
      currentDate.setDate(startOfCalendar.getDate() + i);
      const dateKey = formatDateKey(currentDate);
      const isPast = currentDate < normalizedToday && dateKey !== formatDateKey(normalizedToday);
      const isCurrentMonth = currentDate.getMonth() === displayedMonth.getMonth();
      const dayAvailability = availability.find((day) => day.date === dateKey);
      const hasSlots = dayAvailability?.timeSlots?.some((slot) =>
        canFitSession(dayAvailability.timeSlots, slot.time, currentDurationMinutes)
      ) ?? false;
      const isSelectedDate = currentDate === dateKey;
      const hasScheduledSession = scheduledDates.has(dateKey);

      days.push({
        dateKey,
        label: currentDate.getDate(),
        isPast,
        hasSlots,
        isSelected: isSelectedDate,
        isCurrentMonth,
        hasScheduledSession,
      });
    }

    return days;
  }, [availability, normalizedToday, startOfCalendar, currentDurationMinutes, displayedMonth, selectedSessions]);

  const selectedDaySlots = useMemo(() => {
    if (!currentDate) return [];
    const daySlots = availability.find((day) => day.date === currentDate)?.timeSlots ?? [];

    return daySlots.map((slot) => {
      const canFit = slot.available && canFitSession(daySlots, slot.time, currentDurationMinutes);
      const conflicts = slot.start_utc ? isSlotConflicting(currentDate, slot.start_utc, currentDurationMinutes) : false;

      return {
        ...slot,
        available: canFit && !conflicts,
        conflicted: conflicts,
      };
    });
  }, [availability, currentDate, currentDurationMinutes, selectedSessions]);

  const monthLabel = displayedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    if (!isOpen) {
      setSelectedSessions([]);
      setCurrentUnit(null);
      setCurrentDate('');
      setAvailability([]);
      setError(null);
      setLoadingAvailability(false);
      setIsSaving(false);
      setStep('select-units');
      setDisplayedMonth(new Date());
    }
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadAvailability = async () => {
      if (!isOpen || !guideId) {
        return;
      }

      setLoadingAvailability(true);
      setError(null);

      try {
        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;
        if (!baseUrl) {
          throw new Error('Missing Supabase URL for availability.');
        }

        const today = new Date();
        const startDate = today.toISOString().split('T')[0];
        const endDateObj = new Date(today.getTime() + AVAILABILITY_WINDOW_DAYS * 24 * 60 * 60 * 1000);
        const endDate = endDateObj.toISOString().split('T')[0];

        const response = await fetch(
          `${baseUrl}/functions/v1/guide-availability?guide_id=${guideId}&start_date=${startDate}&end_date=${endDate}`
        );

        if (!response.ok) {
          throw new Error('Unable to load guide availability.');
        }

        const data = await response.json();
        const availabilityDays: AvailabilityDay[] = sortAvailabilityDays(data.availability ?? []);

        if (cancelled) return;

        setAvailability(availabilityDays);
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load availability.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoadingAvailability(false);
        }
      }
    };

    void loadAvailability();

    return () => {
      cancelled = true;
    };
  }, [isOpen, guideId]);

  const handleSelectUnit = (unit: PackageOfferUnit & { offerName: string; offerId: string }) => {
    setCurrentUnit(unit);
    setCurrentDate('');
    setStep('schedule-session');
    setError(null);
  };

  const handleSelectDate = (value: string) => {
    setCurrentDate(value);
    setError(null);
  };

  const handleSelectTime = (slot: AvailabilitySlot & { conflicted?: boolean }) => {
    if (!slot.available || slot.conflicted || !currentUnit || !currentDate) return;

    const newSession: SessionSelection = {
      unit: { ...currentUnit },
      date: currentDate,
      time: slot.time,
      startUtc: slot.start_utc || '',
      durationMinutes: currentDurationMinutes,
    };

    setSelectedSessions([...selectedSessions, newSession]);
    setCurrentUnit(null);
    setCurrentDate('');
    setStep('select-units');
    setError(null);
  };

  const handleRemoveSession = (index: number) => {
    setSelectedSessions(sessions => sessions.filter((_, i) => i !== index));
  };

  const handleBack = () => {
    if (step === 'schedule-session') {
      setCurrentUnit(null);
      setCurrentDate('');
      setStep('select-units');
    }
  };

  const handleSubmit = async () => {
    if (!guideId || !seekerId || selectedSessions.length === 0) {
      setError('Select at least one session to schedule.');
      return;
    }

    // Get the package offer ID from the first session
    const firstUnit = selectedSessions[0].unit as PackageOfferUnit & { offerId?: string };
    const packageOfferId = firstUnit.offerId;
    if (!packageOfferId) {
      setError('Package offer not found.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      const sessions = selectedSessions.map(session => {
        const componentType = session.unit.componentType?.toLowerCase() || '';
        const sessionTypeMap: Record<string, string> = {
          preparation: 'preparation',
          integration: 'integration',
          ceremony: 'journey',
          journey: 'journey',
          additional: 'follow_up',
          follow_up: 'follow_up',
        };

        return {
          session_date: session.date,
          session_time: `${session.time}:00`,
          session_type: sessionTypeMap[componentType] || 'follow_up',
          duration_minutes: session.durationMinutes,
          package_offer_unit_id: session.unit.id,
          notes: `Package unit: ${session.unit.componentName}`,
          location: 'online' as const,
        };
      });

      const { data, error } = await supabase.functions.invoke('batch-schedule-sessions', {
        body: {
          guide_id: guideId,
          seeker_id: seekerId,
          package_offer_id: packageOfferId,
          sessions,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to schedule sessions');
      }

      console.log('Batch sessions scheduled:', data);
      onScheduled(data.sessions.map((s: { id: string }) => s.id));
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save sessions.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const hasAcceptedPackages = acceptedOffers.length > 0 && availableUnits.length > 0;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2C2731]/80 px-3 py-6">
      <div className="w-full max-w-[640px]">
        <div className="flex max-h-[92vh] flex-col overflow-hidden rounded-[32px] border border-[#2C2731] bg-[#FFFCEF] shadow-[0_30px_120px_-40px_rgba(32,77,60,0.65)]">
          <header className="flex h-16 items-center border-b border-[#2C2731]/30 bg-[#2C2731] px-4">
            <button
              type="button"
              onClick={step === 'select-units' ? onClose : handleBack}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              aria-label={step === 'select-units' ? 'Close' : 'Back'}
            >
              {'\u2190'}
            </button>
            <div className="flex flex-1 justify-center">
              <h3 className="text-lg font-semibold text-[#FFFCEF]">
                {step === 'select-units' ? 'Schedule Multiple Sessions' : `Schedule ${currentUnit?.componentName || 'Session'}`}
              </h3>
            </div>
            <div className="w-11" aria-hidden />
          </header>

          <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6">
            {error && (
              <div className="rounded-2xl border border-[#F87171]/60 bg-white/90 px-4 py-3 text-sm text-[#7A1E1E]">
                {error}
              </div>
            )}

            {(!guideId || !seekerId) && (
              <div className="rounded-2xl border border-[#F97316]/50 bg-white/85 px-4 py-3 text-sm text-[#7C4A03]">
                Invite this seeker to create an account before scheduling sessions.
              </div>
            )}

            {!hasAcceptedPackages && (
              <div className="rounded-2xl border border-[#F97316]/50 bg-white/85 px-4 py-3 text-sm text-[#7C4A03]">
                No accepted packages with available units to schedule.
              </div>
            )}

            {step === 'select-units' && hasAcceptedPackages && (
              <>
                {/* Already scheduled sessions in this batch */}
                {selectedSessions.length > 0 && (
                  <section className="rounded-3xl border border-[#2C2731]/20 bg-white/90 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70 mb-3">
                      Sessions to Schedule ({selectedSessions.length})
                    </p>
                    <div className="space-y-2">
                      {selectedSessions.map((session, index) => (
                        <div
                          key={`${session.unit.id}-${session.date}-${session.time}`}
                          className="flex items-center justify-between rounded-2xl border border-[#2C2731]/10 bg-[#FFFCEF] px-4 py-3"
                        >
                          <div>
                            <p className="font-medium text-[#2C2731]">{session.unit.componentName}</p>
                            <p className="text-sm text-[#2C2731]/70">
                              {formatDateForDisplay(session.date)} at {session.time.slice(0, 5)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveSession(index)}
                            className="p-2 text-[#2C2731]/50 hover:text-[#F87171] transition"
                            aria-label="Remove session"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Remaining units to schedule */}
                {remainingUnits.length > 0 && (
                  <section className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                      Add a Session
                    </p>
                    {remainingUnits.map((unit) => (
                      <button
                        key={unit.id}
                        type="button"
                        onClick={() => handleSelectUnit(unit)}
                        className="w-full rounded-2xl border border-[#2C2731]/20 bg-white p-4 text-left transition hover:border-[#2C2731] hover:bg-white/90 flex items-center justify-between"
                      >
                        <div>
                          <h4 className="font-semibold text-[#2C2731]">{unit.componentName}</h4>
                          <p className="mt-1 text-sm text-[#2C2731]/70">
                            {unit.offerName} - {getUnitDuration(unit)} min
                          </p>
                        </div>
                        <PlusIcon className="h-5 w-5 text-[#2C2731]/50" />
                      </button>
                    ))}
                  </section>
                )}

                {remainingUnits.length === 0 && selectedSessions.length > 0 && (
                  <p className="text-sm text-[#2C2731]/70 text-center py-4">
                    All available units have been scheduled
                  </p>
                )}
              </>
            )}

            {step === 'schedule-session' && currentUnit && (
              <>
                <section className="rounded-[32px] border border-[#2C2731]/20 bg-white/90 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                    Scheduling
                  </p>
                  <h1 className="mt-1 text-lg font-semibold text-[#2C2731]">
                    {currentUnit.componentName}
                  </h1>
                  <p className="text-sm text-[#2C2731]/70">
                    {currentUnit.componentType} - {currentDurationMinutes} minutes
                  </p>
                </section>

                {loadingAvailability ? (
                  <div className="rounded-[28px] border border-[#2C2731]/15 bg-white/80 p-6">
                    <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#E8DFBD]" />
                    <div className="mt-4 h-40 animate-pulse rounded-[24px] bg-[#E8DFBD]" />
                  </div>
                ) : (
                  <>
                    <div className="rounded-3xl border border-[#2C2731]/15 bg-white/85 p-5">
                      <div className="flex items-center justify-between text-[#2C2731]">
                        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                          Select a date
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={goToPrevMonth}
                            disabled={!canGoPrevMonth}
                            className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Previous month"
                          >
                            <ChevronLeftIcon className="h-5 w-5" />
                          </button>
                          <span className="min-w-[120px] text-center text-sm font-medium text-[#2C2731]/70">{monthLabel}</span>
                          <button
                            type="button"
                            onClick={goToNextMonth}
                            className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10"
                            aria-label="Next month"
                          >
                            <ChevronRightIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-[#2C2731]/60">All times in {guideTimezone}</p>
                      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-[#2C2731]/70">
                        {DAY_LABELS.map((label) => (
                          <div key={label}>{label}</div>
                        ))}
                      </div>
                      <div className="mt-2 grid grid-cols-7 gap-1">
                        {calendarDays.map((day) => (
                          <button
                            key={day.dateKey}
                            type="button"
                            onClick={() => (!day.isPast && day.hasSlots ? handleSelectDate(day.dateKey) : null)}
                            disabled={day.isPast || !day.hasSlots}
                            className={[
                              'relative h-10 rounded-xl border text-sm font-semibold transition disabled:cursor-not-allowed',
                              currentDate === day.dateKey
                                ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                                : day.hasSlots && day.isCurrentMonth && !day.isPast
                                  ? 'border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731] hover:border-[#2C2731]'
                                  : 'border-transparent bg-transparent text-[#C7C1AD]',
                            ].join(' ')}
                          >
                            {day.label}
                            {day.hasScheduledSession && (
                              <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[#22C55E]" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {currentDate && (
                      <div className="rounded-3xl border border-[#2C2731]/15 bg-[#FFFCEF] p-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                          Available times on {formatDateForDisplay(currentDate)}
                        </p>
                        {selectedDaySlots.length > 0 ? (
                          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {selectedDaySlots.map((slot) => (
                              <button
                                key={slot.start_utc || slot.time}
                                type="button"
                                onClick={() => handleSelectTime(slot)}
                                disabled={!slot.available}
                                className={[
                                  'rounded-full border px-3 py-2 text-sm font-semibold transition',
                                  slot.available
                                    ? 'border-[#2C2731]/20 bg-white text-[#2C2731] hover:border-[#2C2731] hover:bg-[#2C2731] hover:text-[#FFFCEF]'
                                    : slot.conflicted
                                      ? 'cursor-not-allowed border-[#F97316]/40 bg-[#FEF3C7] text-[#92400E]'
                                      : 'cursor-not-allowed border-[#C7C1AD]/40 bg-[#F4EAD1] text-[#C7C1AD] line-through',
                                ].join(' ')}
                                title={slot.conflicted ? 'Conflicts with another session in this batch' : undefined}
                              >
                                {formatTimeForDisplay(slot, guideTimezone)}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-3 text-sm text-[#2C2731]/70">
                            No available times for this date.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </main>

          <footer className="flex items-center gap-3 border-t border-[#2C2731]/20 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-6 pt-4">
            {step === 'select-units' && selectedSessions.length > 0 && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#2C2731] bg-[#2C2731] px-6 py-4 text-lg font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
              >
                <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
                <span>
                  {isSaving
                    ? 'Scheduling...'
                    : `Schedule ${selectedSessions.length} Session${selectedSessions.length > 1 ? 's' : ''}`}
                </span>
                <span className="w-5 text-xl leading-none">{'\u2192'}</span>
              </button>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
};

export default BatchScheduleModal;
